// The Classics category: each day has a classic instrument family (piano, strings, guitar, solo
// strings, brass, flute or e-guitar) on its own slot — its main sound plus two alternates, like
// the other instruments. Chosen per kit by these weights (family mains).
import type { KitId } from "./kits";
import { familyOf, getPreset, SYNTH_PRESETS } from "./presets";
import { mixSeed, Rng } from "./rng";

export const CLASSIC_PAIRINGS: Record<KitId, Record<string, number>> = {
  jungle: {
    "grand-piano": 2,
    "violin-section": 2,
    "solo-violin": 2,
    trumpet: 1,
    "concert-flute": 1,
    "e-guitar": 1,
    vibraphone: 2,
    "tenor-sax": 1,
    "solo-cello": 1,
  },
  deep_sea: {
    "grand-piano": 2,
    "classical-guitar": 2,
    "concert-flute": 2,
    trumpet: 2,
    "violin-section": 1,
    vibraphone: 2,
    clarinet: 1,
    "solo-cello": 2,
    oboe: 1,
  },
  trap_lab: {
    "grand-piano": 3,
    "violin-section": 2,
    "solo-violin": 2,
    "concert-flute": 2,
    "e-guitar": 1,
    "tenor-sax": 2,
    "solo-cello": 2,
  },
  deep_house: {
    "grand-piano": 3,
    trumpet: 2,
    "concert-flute": 2,
    "violin-section": 1,
    "classical-guitar": 1,
    "alto-sax": 3,
    vibraphone: 2,
    "solo-cello": 1,
    oboe: 1,
  },
  techno: {
    "violin-section": 2,
    "grand-piano": 2,
    "e-guitar": 2,
    trumpet: 1,
    "solo-violin": 1,
    "solo-cello": 2,
    harpsichord: 1,
  },
  disco: {
    "violin-section": 3,
    trumpet: 3,
    "grand-piano": 2,
    "e-guitar": 2,
    "concert-flute": 1,
    "alto-sax": 3,
    vibraphone: 1,
    harpsichord: 2,
  },
  garage: {
    "grand-piano": 3,
    "e-guitar": 2,
    "classical-guitar": 1,
    "concert-flute": 1,
    "solo-violin": 1,
    "alto-sax": 2,
    vibraphone: 1,
    harpsichord: 1,
  },
  afro: {
    "classical-guitar": 3,
    "concert-flute": 3,
    trumpet: 2,
    "grand-piano": 1,
    oboe: 1,
    vibraphone: 2,
    "alto-sax": 1,
  },
  synthwave: {
    "e-guitar": 4,
    "grand-piano": 2,
    "violin-section": 2,
    trumpet: 1,
    "tenor-sax": 3,
    vibraphone: 1,
    harpsichord: 1,
  },
};

/** Weighted pick from `weights`, skipping `exclude` (unless nothing else is left). */
export function pickFrom(rng: Rng, weights: Record<string, number>, exclude: readonly string[] = []): string {
  const all = Object.entries(weights).map(([item, weight]) => ({ item, weight }));
  const items = all.filter((x) => !exclude.includes(x.item));
  return rng.weighted(items.length ? items : all);
}

/** Non-classic sounds that sound like a classic family: a day never has both (two flutes or two
 *  brass sections would make every song's melody the same instrument). */
export const CLASSIC_TWINS: Record<string, readonly string[]> = {
  "classic-flute": ["flute-lead", "funk-whistle"],
  "classic-brass": ["ladder-brass", "brass-section", "brass-poly"],
  "orchestral-strings": ["string-section", "string-pad", "synth-strings", "string-ensemble"],
  "solo-strings": ["string-section", "synth-strings", "string-ensemble"],
  cellos: ["string-section", "synth-strings", "string-ensemble"],
  mallets: ["warm-vibes"],
};

const resolve = (id: string) => {
  try {
    return getPreset(id).id;
  } catch {
    return id;
  }
};

/** Does the classic sound `classicId` double the instrument `otherId` (e.g. Concert Flute and Flute)? */
export function soundsAlike(classicId: string, otherId: string): boolean {
  const twins = CLASSIC_TWINS[getPreset(classicId).family] ?? [];
  return twins.includes(resolve(otherId));
}

/**
 * The classic instrument of a day planned without one (its own stream of the day's seed). Its
 * family must not double the day's other instruments (`others`: their mains — every sound of their
 * families counts, since songs may pick an alternate).
 */
export function defaultClassic(
  kit: KitId,
  seed: number,
  exclude: readonly string[] = [],
  others: readonly string[] = [],
): string {
  const near = others.flatMap((id) => {
    try {
      return familyOf(id).map((p) => p.id);
    } catch {
      return [resolve(id)];
    }
  });
  const ok = Object.fromEntries(
    Object.entries(CLASSIC_PAIRINGS[kit]).filter(([id]) => !near.some((o) => soundsAlike(id, o))),
  );
  return pickFrom(new Rng(mixSeed(seed >>> 0, 0xc1a5)), Object.keys(ok).length ? ok : CLASSIC_PAIRINGS[kit], exclude);
}

/**
 * The day's two other classic instruments: from two families other than the main's and each
 * other's (a piano day may offer a violin and a trumpet — not three pianos), so the songs of a day
 * sound different. Weighted by how well a family suits the kit (CLASSIC_PAIRINGS), family mains
 * before their alternate-only sounds, never doubling the day's other instruments (`others`).
 */
export function classicAlternates(
  main: string,
  seed: number,
  kit: KitId,
  others: readonly string[] = [],
): [string, string] {
  const mainFamily = getPreset(main).family;
  const near = others.flatMap((id) => {
    try {
      return familyOf(id).map((p) => p.id);
    } catch {
      return [resolve(id)];
    }
  });
  const familyWeight = new Map<string, number>();
  for (const [id, w] of Object.entries(CLASSIC_PAIRINGS[kit])) {
    const f = getPreset(id).family;
    familyWeight.set(f, Math.max(familyWeight.get(f) ?? 0, w));
  }
  const pool = SYNTH_PRESETS.filter(
    (p) => p.role === "classic" && p.family !== mainFamily && !near.some((o) => soundsAlike(p.id, o)),
  );
  const rng = new Rng(mixSeed(seed >>> 0, 0xa17c1a));
  const pick = (exclude: string[]) => {
    const items = pool
      .filter((p) => !exclude.includes(p.family))
      .map((p) => ({ item: p, weight: (familyWeight.get(p.family) ?? 0.4) * (p.alternateOnly ? 0.5 : 1) }));
    return items.length ? rng.weighted(items) : null;
  };
  const a = pick([]);
  const b = a ? pick([a.family]) : null;
  // (Only if the catalogue ever ran out of families: fall back to the main's own family.)
  if (!a || !b)
    return familyOf(main)
      .filter((p) => p.id !== main)
      .slice(0, 2)
      .map((p) => p.id) as [string, string];
  return [a.id, b.id];
}
